/**
 * Typed client for the admin surface of merchants.yaml v1.2.0 (M2-15, §6.2; visibility filter M6-19 §7).
 */

import type { ApiClient } from "./client";
import { queryString, type Paginated, type Ulid } from "./contracts/common";
import { multipartOverride } from "./multipart";
import type {
  AdminMerchantCategory,
  AdminMerchantCategoryList,
  Branch,
  BranchWrite,
  CustomerAppVisibleFilter,
  EngagementEventType,
  EngagementGroupBy,
  EngagementReportRow,
  Merchant,
  MerchantApplication,
  MerchantApplicationStatus,
  MerchantCategoryCreate,
  MerchantCategoryIconList,
  MerchantCategoryList,
  MerchantCategoryOrderWrite,
  MerchantCategoryPatch,
  MerchantCreate,
  MerchantPatch,
  MerchantSort,
  MerchantStatus,
  MediaKind,
  MenuImage,
  MenuImageList,
  MenuImageOrderWrite,
  OwnerPasswordResetWrite,
  Staff,
  StaffStatus,
  StaffWrite,
} from "./contracts/merchants";

export interface ListMerchantsQuery {
  readonly page?: number;
  readonly per_page?: number;
  readonly search?: string;
  readonly status?: MerchantStatus;
  readonly category_id?: Ulid;
  readonly zone_id?: Ulid;
  /**
   * v1.2.0 tri-state §7.1 directory-visibility filter — omitted = all
   * merchants, `"true"` = visible in the customer app, `"false"` = hidden.
   * Sent as the contract's string literal, never a boolean.
   */
  readonly customer_app_visible?: CustomerAppVisibleFilter;
  /** v1.1.0 — whitelisted `<field>:<asc|desc>`; omitted = created_at:desc. */
  readonly sort?: MerchantSort;
}

/** v1.1.0 — the directory `category_id` references (public read, unpaginated). */
export function listMerchantCategories(client: ApiClient): Promise<MerchantCategoryList> {
  return client.request({ method: "GET", path: "/merchant-categories" });
}

/* ------------------------------------------------------------------ */
/* Store categories — admin CRUD (M7-148, `merchants.category.*`)       */
/* ------------------------------------------------------------------ */

/**
 * The whole table including hidden rows — the panel's read everywhere a
 * category must be NAMED (a store on a hidden category still has to render)
 * as well as on the management screen itself.
 */
export function listAdminMerchantCategories(client: ApiClient): Promise<AdminMerchantCategoryList> {
  return client.request({ method: "GET", path: "/admin/merchant-categories" });
}

/** The allowed `icon` keys — the picker renders a glyph per key. */
export function listMerchantCategoryIcons(client: ApiClient): Promise<MerchantCategoryIconList> {
  return client.request({ method: "GET", path: "/admin/merchant-categories/icons" });
}

export function createMerchantCategory(client: ApiClient, body: MerchantCategoryCreate): Promise<AdminMerchantCategory> {
  return client.request({ method: "POST", path: "/admin/merchant-categories", body });
}

export function updateMerchantCategory(
  client: ApiClient,
  categoryId: Ulid,
  body: MerchantCategoryPatch,
): Promise<AdminMerchantCategory> {
  return client.request({ method: "PATCH", path: `/admin/merchant-categories/${categoryId}`, body });
}

/** Refused with `422 CATEGORY_IN_USE` while any store is assigned — hide instead. */
export function deleteMerchantCategory(client: ApiClient, categoryId: Ulid): Promise<null> {
  return client.request({ method: "DELETE", path: `/admin/merchant-categories/${categoryId}` });
}

/** Full ordered id list, idempotent — `422 CATEGORY_ORDER_INCOMPLETE` if partial. */
export function reorderMerchantCategories(
  client: ApiClient,
  body: MerchantCategoryOrderWrite,
): Promise<AdminMerchantCategoryList | null> {
  return client.request({ method: "PUT", path: "/admin/merchant-categories/order", body });
}

/**
 * Replace the category picture. Declared as a multipart PUT; PHP parses
 * multipart on POST only, so it travels as `POST` +
 * `X-HTTP-Method-Override: PUT` — the same encoding as the store media.
 */
export function putMerchantCategoryImage(client: ApiClient, categoryId: Ulid, body: FormData): Promise<AdminMerchantCategory> {
  return client.request(multipartOverride({ method: "PUT", path: `/admin/merchant-categories/${categoryId}/image`, body }));
}

export function deleteMerchantCategoryImage(client: ApiClient, categoryId: Ulid): Promise<AdminMerchantCategory | null> {
  return client.request({ method: "DELETE", path: `/admin/merchant-categories/${categoryId}/image` });
}

export function listMerchants(client: ApiClient, query: ListMerchantsQuery): Promise<Paginated<Merchant>> {
  return client.request({ method: "GET", path: `/admin/merchants${queryString({ ...query })}` });
}

export function getMerchant(client: ApiClient, merchantId: Ulid): Promise<Merchant> {
  return client.request({ method: "GET", path: `/admin/merchants/${merchantId}` });
}

export function createMerchant(client: ApiClient, body: MerchantCreate): Promise<Merchant> {
  return client.request({ method: "POST", path: "/admin/merchants", body });
}

export function updateMerchant(client: ApiClient, merchantId: Ulid, body: MerchantPatch): Promise<Merchant> {
  return client.request({ method: "PATCH", path: `/admin/merchants/${merchantId}`, body });
}

export function listBranches(
  client: ApiClient,
  merchantId: Ulid,
  query: { readonly page?: number; readonly per_page?: number },
): Promise<Paginated<Branch>> {
  return client.request({ method: "GET", path: `/admin/merchants/${merchantId}/branches${queryString({ ...query })}` });
}

export function createBranch(client: ApiClient, merchantId: Ulid, body: BranchWrite): Promise<Branch> {
  return client.request({ method: "POST", path: `/admin/merchants/${merchantId}/branches`, body });
}

export function updateBranch(client: ApiClient, merchantId: Ulid, branchId: Ulid, body: BranchWrite): Promise<Branch> {
  return client.request({ method: "PATCH", path: `/admin/merchants/${merchantId}/branches/${branchId}`, body });
}

/** Soft delete — may drop the merchant out of order-readiness (§6.2); UI must confirm (§14.3). */
export function deactivateBranch(client: ApiClient, merchantId: Ulid, branchId: Ulid): Promise<void> {
  return client.request({ method: "DELETE", path: `/admin/merchants/${merchantId}/branches/${branchId}` });
}

/**
 * merchants.yaml v1.21.0 (M7-326) — set a NEW password on the merchant's
 * owner account; `merchants.merchant.manage`. 204: every session of the
 * owner is revoked (signed out everywhere). 422 `NO_OWNER_ACCOUNT` when the
 * merchant has no owner account. The password is the body and nothing else —
 * never a query string, never logged (§11.1).
 */
export function resetMerchantOwnerPassword(
  client: ApiClient,
  merchantId: Ulid,
  body: OwnerPasswordResetWrite,
): Promise<void> {
  return client.request({ method: "POST", path: `/admin/merchants/${merchantId}/owner/password-reset`, body });
}

/* ------------------------------------------------------------------ */
/* Store media — merchants.yaml v1.9.0 (Wave 16), `merchants.merchant.manage` */
/* ------------------------------------------------------------------ */

/**
 * Replace the logo or the cover. The contract declares a multipart PUT; PHP
 * parses multipart on POST only, so the call travels as `POST` +
 * `X-HTTP-Method-Override: PUT` (merchants.yaml info.description).
 */
export function putMerchantMedia(client: ApiClient, merchantId: Ulid, kind: MediaKind, body: FormData): Promise<Merchant> {
  return client.request(multipartOverride({ method: "PUT", path: `/admin/merchants/${merchantId}/media/${kind}`, body }));
}

export function deleteMerchantMedia(client: ApiClient, merchantId: Ulid, kind: MediaKind): Promise<Merchant | null> {
  return client.request({ method: "DELETE", path: `/admin/merchants/${merchantId}/media/${kind}` });
}

/** The ordered gallery, whole — the panel's read for the menu tab. */
export function listMenuImages(client: ApiClient, merchantId: Ulid): Promise<MenuImageList> {
  return client.request({ method: "GET", path: `/admin/merchants/${merchantId}/menu-images` });
}

/** One image per call, appended at the end. v1.12.0: there is no cap. */
export function uploadMenuImage(client: ApiClient, merchantId: Ulid, body: FormData): Promise<MenuImage> {
  return client.request({ method: "POST", path: `/admin/merchants/${merchantId}/menu-images`, body });
}

export function deleteMenuImage(client: ApiClient, merchantId: Ulid, imageId: Ulid): Promise<null> {
  return client.request({ method: "DELETE", path: `/admin/merchants/${merchantId}/menu-images/${imageId}` });
}

/** Full ordered id list, idempotent — the M7-106 banner reorder shape. */
export function reorderMenuImages(client: ApiClient, merchantId: Ulid, body: MenuImageOrderWrite): Promise<MenuImageList | null> {
  return client.request({ method: "PUT", path: `/admin/merchants/${merchantId}/menu-images/order`, body });
}

/**
 * `logo_url` / `cover_url` / `image_url` may arrive absolute or origin-relative
 * — the same rule as the banner images, anchored on the API origin.
 */
export { resolveBannerImageUrl as resolveMediaUrl } from "./marketingApi";

/** The multipart body every media write carries: exactly one `image` part. */
export function mediaFormData(image: File): FormData {
  const form = new FormData();
  form.set("image", image, image.name);
  return form;
}

export interface ListStaffQuery {
  readonly page?: number;
  readonly per_page?: number;
  readonly status?: StaffStatus;
}

export function listStaff(client: ApiClient, merchantId: Ulid, query: ListStaffQuery): Promise<Paginated<Staff>> {
  return client.request({ method: "GET", path: `/admin/merchants/${merchantId}/staff${queryString({ ...query })}` });
}

export function addStaff(client: ApiClient, merchantId: Ulid, body: StaffWrite): Promise<Staff> {
  return client.request({ method: "POST", path: `/admin/merchants/${merchantId}/staff`, body });
}

export function removeStaff(client: ApiClient, merchantId: Ulid, staffId: Ulid): Promise<void> {
  return client.request({ method: "DELETE", path: `/admin/merchants/${merchantId}/staff/${staffId}` });
}

/**
 * §7.2 cross-merchant engagement report (M7-38) — permission
 * `merchants.engagement.view_all`. Aggregated per store / category / zone
 * from `engagement_daily_rollups` (never raw events, §11.2); rows arrive
 * sorted by `total` DESC. Date bounds are inclusive `YYYY-MM-DD` days on the
 * deployment-local calendar (merchants.yaml DateFrom/DateTo, max 366 days).
 */
export interface EngagementReportQuery {
  readonly page?: number;
  readonly per_page?: number;
  readonly group_by: EngagementGroupBy;
  readonly event_type?: EngagementEventType;
  readonly from?: string;
  readonly to?: string;
}

export function getEngagementReports(
  client: ApiClient,
  query: EngagementReportQuery,
): Promise<Paginated<EngagementReportRow>> {
  return client.request({ method: "GET", path: `/admin/engagement/reports${queryString({ ...query })}` });
}

/* ------------------------------------------------------------------ */
/* Merchant applications — §14.5 join flow, admin review (M7-43)        */
/* ------------------------------------------------------------------ */

/**
 * merchants.yaml v1.4.0 (M7-42) — the admin review surface:
 *   GET  /admin/merchant-applications?status=                 `merchants.application.view`
 *   POST /admin/merchant-applications/{applicationId}/approve `merchants.application.decide`
 *   POST /admin/merchant-applications/{applicationId}/reject {reason}
 * Decisions are single-shot atomic claims (409 APPLICATION_ALREADY_DECIDED).
 * Approval materializes the ACTIVE merchant + branch from the canonicalized
 * `store` payload (applicant becomes owner; zone re-validated — 422
 * BRANCH_ZONE_INVALID leaves the row PENDING); rejection stores the required
 * reason the applicant reads in the app.
 */
export interface ListMerchantApplicationsQuery {
  readonly page?: number;
  readonly per_page?: number;
  readonly status?: MerchantApplicationStatus;
}

export function listMerchantApplications(
  client: ApiClient,
  query: ListMerchantApplicationsQuery,
): Promise<Paginated<MerchantApplication>> {
  return client.request({ method: "GET", path: `/admin/merchant-applications${queryString({ ...query })}` });
}

export function approveMerchantApplication(client: ApiClient, applicationId: Ulid): Promise<MerchantApplication> {
  return client.request({ method: "POST", path: `/admin/merchant-applications/${applicationId}/approve` });
}

export function rejectMerchantApplication(
  client: ApiClient,
  applicationId: Ulid,
  body: { readonly reason: string },
): Promise<MerchantApplication> {
  return client.request({ method: "POST", path: `/admin/merchant-applications/${applicationId}/reject`, body });
}
